import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  useDeleteNotificationRuleMutation,
  useGetNotificationRuleEvaluationQuery,
  useGetNotificationRuleQuery,
  useGetNotificationRuleTriggerStateQuery,
  useListExternalMessagingChannelsQuery,
  useSendTestNotificationRuleMutation,
  useUpdateNotificationRuleTriggerOccurrenceMutation,
  useUpdateNotificationRuleMutation
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { StatusPill } from '../../../components/primitives/StatusPill'
import { NotificationRuleForm } from '../../../components/notification-rules/NotificationRuleForm'
import {
  indexTriggerEvals,
  TriggerRulesEditor
} from '../../../components/triggers/TriggerRulesEditor'
import { StoredTriggerStateList } from '../../../components/triggers/StoredTriggerStateList'
import { ConfirmDialog } from '../components/DeleteConfirmDialog'
import { DetailError, DetailLoading, InlineAlert } from '../components/DetailStates'
import { DetailHeader } from '../components/DetailHeader'
import { EquipmentScope } from '../components/EquipmentScope'
import { InfoStrip } from '../components/InfoStrip'
import { ConfigLabelsSection } from '../components/ConfigLabelsSection'
import { formatDate, notificationRuleStateToStatus } from '../util/format'

interface RtkQueryErrorShape {
  status?: number
  data?: unknown
}

function proxyApiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as RtkQueryErrorShape | undefined)?.data
  if (typeof data === 'object' && data !== null && 'error' in data) {
    const msg = (data as { error?: unknown }).error
    if (typeof msg === 'string' && msg.length > 0) return msg
  }
  if (error instanceof Error && error.message) return error.message
  return fallback
}

interface NotificationRuleDetailProps {
  id: string
}

const NotificationRuleDetail = ({ id }: NotificationRuleDetailProps) => {
  const navigate = useNavigate()
  const [editing, setEditing] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const { data: rule, isLoading, error } = useGetNotificationRuleQuery(id, { skip: !id })
  const { data: triggerState } = useGetNotificationRuleTriggerStateQuery(
    { notificationRuleId: id },
    {
      skip: !id || editing,
      pollingInterval: 5000,
      skipPollingIfUnfocused: true
    }
  )
  const { data: evaluation } = useGetNotificationRuleEvaluationQuery(
    { notificationRuleId: id },
    {
      skip: !id || editing,
      pollingInterval: 5000,
      skipPollingIfUnfocused: true
    }
  )
  const evalByTriggerId = useMemo(
    () => indexTriggerEvals(evaluation?.triggers),
    [evaluation?.triggers]
  )
  const { data: channels = [] } = useListExternalMessagingChannelsQuery({
    platform: 'teams',
    page_size: 500
  })
  const teamsChannel = useMemo(
    () => channels.find((c) => c.id === rule?.teams_channel_id),
    [channels, rule?.teams_channel_id]
  )

  const [updateRule, { isLoading: updating }] = useUpdateNotificationRuleMutation()
  const [deleteRule, { isLoading: deleting }] = useDeleteNotificationRuleMutation()
  const [sendTest, { isLoading: sendingTest }] = useSendTestNotificationRuleMutation()
  const [updateOccurrence, { isLoading: updatingOccurrence }] =
    useUpdateNotificationRuleTriggerOccurrenceMutation()

  useEffect(() => {
    if (!editing) setActionError(null)
  }, [editing])

  const goBack = () => void navigate('/configure?type=notification-rules')

  if (isLoading) return <DetailLoading message="Loading notification rule…" />
  if (error || !rule) return <DetailError message="Failed to load notification rule." />

  const toggleDisabled = async () => {
    try {
      setActionError(null)
      const nextState = rule.state === 'disabled' ? 'active' : 'disabled'
      await updateRule({ id: rule.id, patch: { state: nextState } }).unwrap()
    } catch (e) {
      setActionError(proxyApiErrorMessage(e, 'Failed to update rule state.'))
    }
  }

  const doDelete = async () => {
    try {
      setActionError(null)
      await deleteRule(id).unwrap()
      setDeleteOpen(false)
      goBack()
    } catch (e) {
      setActionError(proxyApiErrorMessage(e, 'Failed to delete rule.'))
      setDeleteOpen(false)
    }
  }

  const doSendTest = async () => {
    try {
      setActionError(null)
      const result = await sendTest(id).unwrap()
      if (result.sent) {
        toast('Test notification sent', {
          description: 'Delivered to the rule\u2019s configured email(s) / Teams channel.'
        })
      } else {
        toast('Test notification not sent', {
          description: 'The rule has no delivery target configured.'
        })
      }
    } catch (e) {
      setActionError(proxyApiErrorMessage(e, 'Failed to send test notification.'))
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <DetailHeader
        title={rule.display_name}
        backLabel="Notification rules"
        onBack={goBack}
        trailing={
          <StatusPill status={notificationRuleStateToStatus(rule.state)}>{rule.state}</StatusPill>
        }
        actions={
          editing ? null : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void doSendTest()}
                disabled={sendingTest}
              >
                {sendingTest ? 'Sending…' : 'Send test'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void toggleDisabled()}
                disabled={updating}
              >
                {rule.state === 'disabled' ? 'Enable' : 'Disable'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDeleteOpen(true)}
                disabled={deleting}
              >
                Delete
              </Button>
            </>
          )
        }
      />

      {actionError && <InlineAlert kind="down">{actionError}</InlineAlert>}

      {editing ? (
        <Card className="px-6">
          <NotificationRuleForm
            initialValue={rule}
            busy={updating}
            submitLabel="Save changes"
            onSubmit={async (payload) => {
              await updateRule({ id: rule.id, patch: payload }).unwrap()
              setEditing(false)
            }}
            onCancel={() => setEditing(false)}
          />
        </Card>
      ) : (
        <>
          {rule.message_title && (
            <p className="m-0 text-sm font-medium leading-relaxed text-ink">{rule.message_title}</p>
          )}
          {rule.message_body && (
            <p className="m-0 whitespace-pre-wrap text-sm leading-relaxed text-ink-2">
              {rule.message_body}
            </p>
          )}
          {(rule.clear_message_title || rule.clear_message_body) && (
            <div className="rounded-lg border border-line bg-card px-4 py-3">
              <p className="m-0 text-xs font-medium uppercase tracking-wide text-ink-4">
                Return to normal
              </p>
              {rule.clear_message_title && (
                <p className="mt-1 mb-0 text-sm font-medium text-ink">{rule.clear_message_title}</p>
              )}
              {rule.clear_message_body && (
                <p className="mt-1 mb-0 whitespace-pre-wrap text-sm text-ink-2">
                  {rule.clear_message_body}
                </p>
              )}
            </div>
          )}

          <ConfigLabelsSection
            labels={rule.labels ?? []}
            onChange={async (next) => {
              try {
                setActionError(null)
                await updateRule({
                  id: rule.id,
                  patch: { labels: next }
                }).unwrap()
              } catch (e) {
                setActionError(proxyApiErrorMessage(e, 'Failed to update labels.'))
                throw e
              }
            }}
          />

          <InfoStrip
            items={[
              {
                label: 'Email',
                value:
                  rule.email_addresses.length > 0
                    ? rule.email_addresses.join(', ')
                    : 'None configured'
              },
              {
                label: 'Teams channel',
                value: teamsChannel
                  ? `${teamsChannel.channel_name ?? teamsChannel.channel_id}${
                      teamsChannel.team_name ? ` (${teamsChannel.team_name})` : ''
                    }`
                  : rule.teams_channel_id
                    ? rule.teams_channel_id
                    : 'None configured'
              },
              { label: 'Re-arm guard', value: `${rule.cooldown_seconds}s` },
              {
                label: 'Trigger events',
                value:
                  rule.trigger_events == null
                    ? 'Legacy level dispatch'
                    : rule.trigger_events.length
                      ? rule.trigger_events.join(', ')
                      : 'No edge notifications'
              },
              {
                label: 'Last notified',
                value: formatDate(rule.last_notified_at)
              },
              { label: 'Updated', value: formatDate(rule.updated_at) }
            ]}
          />

          <Section label="Watching" hint="Equipment and signals this rule reads from.">
            <EquipmentScope tagPaths={rule.tag_paths ?? []} />
          </Section>

          <Section
            label="Stored trigger state"
            hint="Each trigger has independent activation and clear timing."
          >
            {triggerState ? (
              <StoredTriggerStateList
                triggers={rule.triggers}
                states={triggerState.triggers}
                occurrences={triggerState.occurrences}
                actionBusy={updatingOccurrence}
                onOccurrenceAction={(occurrenceId, action) => {
                  setActionError(null)
                  void updateOccurrence({
                    notificationRuleId: rule.id,
                    occurrenceId,
                    action
                  })
                    .unwrap()
                    .catch((error: unknown) =>
                      setActionError(
                        proxyApiErrorMessage(error, 'Failed to update trigger occurrence.')
                      )
                    )
                }}
              />
            ) : (
              <p className="m-0 text-sm text-ink-3">
                Stored state is unavailable until the poller evaluates these triggers.
              </p>
            )}
          </Section>

          <Section
            label="When this happens, notify"
            hint="Every trigger is tracked independently; all active triggers count."
          >
            <TriggerRulesEditor
              triggers={rule.triggers}
              tagPaths={rule.tag_paths ?? []}
              evalByTriggerId={evalByTriggerId}
              readOnly
              onChange={() => {
                /* view-only */
              }}
            />
          </Section>
        </>
      )}

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this notification rule?"
        description="This cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={doDelete}
        busy={deleting}
      />
    </div>
  )
}

interface SectionProps {
  label: string
  hint?: string
  children: React.ReactNode
}

function Section({ label, hint, children }: SectionProps) {
  return (
    <section className="flex flex-col gap-3">
      <div className="min-w-0 flex-1">
        <h2 className="m-0 text-sm font-semibold text-ink">{label}</h2>
        {hint && <p className="mt-0.5 m-0 text-xs text-ink-4">{hint}</p>}
      </div>
      {children}
    </section>
  )
}

export default NotificationRuleDetail
